import { useEffect, useState } from "react";
import api from "@/lib/api";
import ThinkingLoader from "@/components/ThinkingLoader";
import { PlayCircle, TrendingUp, MousePointerClick } from "lucide-react";

const CLIP_LABELS = {
  cv: "CV Builder",
  letter: "Motivatiebrief",
  interview: "Interview-coach",
  radar: "Match Radar",
};

/**
 * AdminTourStatsWidget — 30-day product-tour funnel for the admin overview.
 *
 * Reads `/api/admin/tour-stats` (aggregated on the server so no PII touches
 * the client) and renders opens → completes → CTA clicks together with a
 * per-tab popularity ranking and per-milestone playback drop-off strip.
 */
export default function AdminTourStatsWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/admin/tour-stats");
        if (alive) setData(r.data);
      } catch {
        if (alive) setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="ed-card p-6 mt-4" data-testid="admin-tour-stats-loading">
        <ThinkingLoader stage="generic" testId="admin-tour-stats-thinking" />
      </div>
    );
  }
  if (!data) return null;

  const tabsSorted = Object.entries(data.tabs || {}).sort((a, b) => b[1] - a[1]);
  const totalTabs = tabsSorted.reduce((a, [, v]) => a + v, 0);
  const maxTab = tabsSorted.length ? tabsSorted[0][1] : 0;
  const milestones = ["25", "50", "75", "100"];
  const maxMilestone = Math.max(1, ...milestones.map((m) => data.progress?.[m] || 0));

  return (
    <div className="ed-card p-6 mt-4" data-testid="admin-tour-stats">
      <div className="flex items-baseline justify-between gap-3 mb-4 flex-wrap">
        <div>
          <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">
            Product-tour · laatste 30 dagen
          </p>
          <h3 className="font-serif text-xl mt-1">Video-engagement</h3>
        </div>
        <p className="font-mono text-[0.6rem] text-neutral-500">
          completion {data.completion_rate_pct}% · CTA-CTR {data.cta_ctr_pct}%
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="border border-neutral-200 p-3" data-testid="tour-stat-opens">
          <div className="flex items-center gap-1.5 text-neutral-500">
            <PlayCircle size={12} strokeWidth={1.5} />
            <span className="font-mono text-[0.55rem] uppercase tracking-[0.15em]">opens</span>
          </div>
          <p className="font-serif text-2xl mt-1">{data.opens}</p>
        </div>
        <div className="border border-neutral-200 p-3" data-testid="tour-stat-completes">
          <div className="flex items-center gap-1.5 text-neutral-500">
            <TrendingUp size={12} strokeWidth={1.5} />
            <span className="font-mono text-[0.55rem] uppercase tracking-[0.15em]">voltooid</span>
          </div>
          <p className="font-serif text-2xl mt-1">{data.completes}</p>
        </div>
        <div className="border border-neutral-200 p-3" data-testid="tour-stat-cta">
          <div className="flex items-center gap-1.5 text-neutral-500">
            <MousePointerClick size={12} strokeWidth={1.5} />
            <span className="font-mono text-[0.55rem] uppercase tracking-[0.15em]">cta-clicks</span>
          </div>
          <p className="font-serif text-2xl mt-1">{data.cta_clicks}</p>
        </div>
      </div>

      <div className="mb-6">
        <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 mb-2">
          Populariteit per tab
        </p>
        {tabsSorted.length === 0 ? (
          <p className="text-sm text-neutral-500">Nog geen tab-switches gemeten.</p>
        ) : (
          <ul className="space-y-1.5">
            {tabsSorted.map(([key, n]) => {
              const pct = totalTabs ? Math.round((n / totalTabs) * 100) : 0;
              const barPct = maxTab ? Math.max(4, Math.round((n / maxTab) * 100)) : 0;
              return (
                <li key={key} data-testid={`tour-tab-stat-${key}`} className="flex items-center gap-3">
                  <span className="w-32 font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-700 shrink-0">
                    {CLIP_LABELS[key] || key}
                  </span>
                  <div className="flex-1 bg-neutral-100 h-3 relative">
                    <div
                      className="bg-neutral-900 h-3"
                      style={{ width: `${barPct}%` }}
                    />
                  </div>
                  <span className="font-mono text-[0.65rem] text-neutral-600 w-16 text-right shrink-0">
                    {n} · {pct}%
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div>
        <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 mb-2">
          Kijk-diepte (waar haken kijkers af)
        </p>
        <div className="grid grid-cols-4 gap-2">
          {milestones.map((m) => {
            const v = data.progress?.[m] || 0;
            const pct = Math.max(6, Math.round((v / maxMilestone) * 100));
            return (
              <div key={m} data-testid={`tour-progress-${m}`} className="text-center">
                <div className="flex items-end h-16 justify-center">
                  <div
                    className="w-full bg-amber-300 border border-amber-500/60"
                    style={{ height: `${pct}%` }}
                    title={`${v} kijkers voorbij ${m}%`}
                  />
                </div>
                <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 mt-1">
                  {m}%
                </p>
                <p className="font-serif text-sm">{v}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
